import {inject, Injectable} from '@angular/core';
import {LocalAnalysis} from "../../core/models/local-analysis";
import {
  catchError,
  filter,
  firstValueFrom,
  forkJoin,
  from,
  map,
  Observable,
  of,
  switchMap,
  tap,
  throwError
} from "rxjs";
import {Analysis, AnalysisPostcode} from "../../core/models/analysis";
import {CustomError} from "../../core/models/error";
import {PictureStatus} from "../../core/models/picture";
import {TyreAnalysisRequestDto} from "../../core/dto/tyre-analysis-request-dto";
import {StepType} from "../../core/models/step";
import * as Sentry from "@sentry/angular";
import {Utils} from "../utils/generics.utils";
import {TyreAnalysisResponseDto} from "../../core/dto/tyre-analysis-response-dto";
import {AnalysisStatusEnum} from "../../core/models/analysis-status";
import {isEqual} from "lodash";
import {AnalysisResponseDto} from "../../core/dto/analysis-response-dto";
import {StoreService} from "./store.service";
import {ReferenceApiService} from "../../core/api/reference-api/reference-api.service";
import {PictureUploadService} from "./picture-upload.service";
import {ApiService} from "../../core/api/api.service";
import {IndexedDBService} from "./indexed-db.service";
import {PostcodeService} from "../../features/shared/postcode/postcode.service";
import {TyrePicture} from "../../core/models/tyrePicture";
import {Context} from "../../core/models/context";
import {LanguageService} from "./language.service";

@Injectable({
  providedIn: 'root'
})
export class AnalysisService {

  protected storeService = inject(StoreService);
  protected referenceApi = inject(ReferenceApiService);
  protected postcodeService = inject(PostcodeService);
  protected uploadService = inject(PictureUploadService);
  protected api = inject(ApiService);
  protected indexedDB = inject(IndexedDBService);
  protected languageService = inject(LanguageService);

  createAnalysis(localAnalysis?: LocalAnalysis): Observable<Analysis> {
    const {
      vehicle = this.storeService.getVehicle(),
      context = this.storeService.getContext(),
      company = this.storeService.getCompany(),
      fleet = this.storeService.getFleet(),
      postcode = this.storeService.getPostcode(),
      externalId = this.storeService.getExternalId()
    } = localAnalysis || {};

    if (vehicle.id && company?.id && fleet?.key) {
      return this.referenceApi
        .createAnalysis(
          vehicle.id,
          context,
          company.id,
          fleet.key,
          this.languageService.getLanguage(),
          vehicle.mileage,
          externalId
        )
        .pipe(
          switchMap((analysis) => this.handleAnalysisCreation(analysis, postcode)),
          switchMap((analysis) => this.handleLocalAnalysis(analysis, localAnalysis))
        );
    } else {
      return throwError(() => new Error(CustomError.NO_SUCH_VEHICLE));
    }
  }

  createTyreAnalysis(picture: TyrePicture) {
    const analysisId = this.storeService.getAnalysis()?.id;
    const dto = new TyreAnalysisRequestDto('jpg', picture.step.position, picture.step.type);
    const vehicleId = this.storeService.getVehicle().id;

    if (vehicleId) {
      if (analysisId) {
        return this.referenceApi.getAnalysis(vehicleId, this.storeService.getContext()).pipe(switchMap((analysis) => {
          const steps = picture.step.type === StepType.REFERENCE ? analysis.referenceTyres : analysis.wearTyres;
          const tyreAnalysis = steps.find((ta) => ta.position === picture.step.position);

          if (tyreAnalysis) {
            Sentry.captureMessage('Tried to create an existing tyre analysis', {extra: {picture: Utils.sanitizePictures(picture), tyreAnalysis}});
            return of({
              ...tyreAnalysis,
              id: tyreAnalysis.tyreAnalysisId
            } as TyreAnalysisResponseDto);
          } else {
            return this.referenceApi.createTyreAnalysis(analysisId, dto);
          }
        }))
      } else {
        return this.createAnalysis().pipe(switchMap((analysis) => this.referenceApi.createTyreAnalysis(analysis.id!, dto)))
      }
    } else {
      return throwError(() => new Error(CustomError.NO_SUCH_VEHICLE));
    }
  }

  abortAnalysis() {
    const analysisId = this.storeService.getAnalysis()?.id;
    if (analysisId) {
      return this.referenceApi.updateAnalysis(analysisId, AnalysisStatusEnum.ABORTED)
        .pipe(
          tap(() => this.uploadService.cancelUpload()),
          tap(() => this.storeService.clearLocalPicturesAndAnalysis()),
          switchMap(_ => {
            if (this.storeService.getContext() === Context.COLLECT) {
              this.storeService.clear();
              return of(null);
            } else {
              return this.createAnalysis();
            }
          })
        );
    } else {
      return throwError(() => new Error(CustomError.NO_ANALYSIS));
    }
  }

  getAnalysis() {
    const vehicleId = this.storeService.getVehicle().id;
    if (!vehicleId) {
      return throwError(() => new Error(CustomError.NO_SUCH_VEHICLE));
    }

    return this.referenceApi.getAnalysis(vehicleId, this.storeService.getContext()).pipe(
      switchMap((response) => {
        if (response.status === AnalysisStatusEnum.UPLOADED) {
          return throwError(() => new Error(CustomError.ONGOING_ANALYSIS));
        } else {
          const analysis = new Analysis(response);
          this.storeService.setAnalysis(analysis);
          const pictures = [
            ...analysis.referenceTyres.map(rt => TyrePicture.fromAnalysis(rt, StepType.REFERENCE)),
            ...analysis.wearTyres.map(wt => TyrePicture.fromAnalysis(wt, StepType.WEAR))
          ];
          return forkJoin([this.indexedDB.getAllTyresPictures(), of(pictures), of(analysis)]);
        }
      }),
      switchMap(([localPictures, pictures, analysis]) => {
        return this.handleLocalPictures(localPictures, pictures, analysis)
      })
    );
  }


  private handleLocalPictures(localPictures: TyrePicture[], pictures: TyrePicture[], analysis: Analysis) {
    if (this.storeService.getLocalAnalysis()){
      localPictures.forEach((localPicture) => {
        const picture = pictures.find((p) => isEqual(localPicture.step, p.step))
        if (picture) {
          localPicture = {
            ...picture,
            data: localPicture.data,
            status: localPicture.status
          } as unknown as TyrePicture;
        }
      });
      this.storeService.setPictures(localPictures);
    } else {
      pictures.forEach((picture) => {
        const localPicture = localPictures.find((localPicture) => isEqual(localPicture.step, picture.step))
        if (localPicture) {
          picture.status = localPicture.status;
        }
      });
      this.storeService.setPictures(pictures);
    }
    return of(analysis);
  }


  private handleAnalysisCreation(analysisResponseDto: AnalysisResponseDto, postcode?: AnalysisPostcode): Observable<Analysis> {
    const analysis = new Analysis(analysisResponseDto);
    this.storeService.clearPictures();
    this.storeService.setAnalysis(analysis);
    return this.createContact(analysis).pipe(
      switchMap((analysis) => this.updatePostcode(analysis, postcode))
    );
  }

  private createContact(analysis: Analysis) {
    const phone = this.storeService.getContact().phone;
    const email = this.storeService.getContact().email || undefined;
    if ((phone || email) && analysis?.id) {
      return this.referenceApi.createContact(analysis.id, email, phone).pipe(
        map(() => analysis),
        catchError((err) => {
          if (err.status === 400 && err.error.error === 'RESOURCE_ALREADY_EXIST') {
            return of(analysis);
          }
          Sentry.captureException(() => new Error('Unable to create contact'), {extra: {err}})
          return throwError(() => err);
        }));
    }
    return of(analysis);
  }

  private updatePostcode(analysis: Analysis, postcode?: AnalysisPostcode)  {
    return postcode
      ? this.postcodeService.setPostcode(analysis.id!, postcode).pipe(
        map((response) => {
          analysis.postcode = {
            codeCommune: response.body?.inseeCodeTravelZone,
            codePostal: response.body?.zipCodeTravelZone
          };
          this.storeService.setAnalysis(analysis);
          return analysis;
        })
      )
      : of(analysis);
  }

  private handleLocalAnalysis(analysis: Analysis, localAnalysis?: LocalAnalysis) {
    if (localAnalysis) {
      return from(this.createSpareTyre(analysis, localAnalysis)).pipe(
        switchMap(() => this.indexedDB.getAllTyresPictures()),
        switchMap((pictures) => {
          return this.getPicturesAndCreateTyreAnalyses(pictures, analysis)
        }),
        switchMap((value) => this.uploadPictures(value))
      );
    }
    return of(analysis);
  }

  private createSpareTyre(analysis: Analysis, localAnalysis: LocalAnalysis) {
    if (analysis.id && localAnalysis.spareTyreResult?.spareTyreResult) {
      return this.referenceApi.createSpareTyreAnalysis$(analysis.id).pipe(
        map((response) => response.id),
        filter((vehicleAnalysisId) => !!vehicleAnalysisId),
        switchMap((vehicleAnalysisId) => this.referenceApi.updateSpareTyreAnalysis$(vehicleAnalysisId, localAnalysis.spareTyreResult!.spareTyreResult!).pipe(
          map(() => vehicleAnalysisId))
        ),
        switchMap((vehicleAnalysisId) => this.referenceApi.updateVehicleAnalysis$(vehicleAnalysisId, AnalysisStatusEnum.UPLOADED).pipe(
          map(() => vehicleAnalysisId))
        ),
        tap((vehicleAnalysisId) =>
          // @ts-ignore
          this.storeService.setAnalysis({
            ...this.storeService.getAnalysis(),
            spareTyreResult: {
              vehicleAnalysisId,
              spareTyreResult: localAnalysis.spareTyreResult!.spareTyreResult!,
              status: AnalysisStatusEnum.UPLOADED
            }
          }))
      );
    } else {
      return of(undefined);
    }
  }

  private getPicturesAndCreateTyreAnalyses(pictures: TyrePicture[], analysis: Analysis) {
    pictures = pictures.map((p) => {
      p.status = PictureStatus.UPLOADING;
      return p;
    });
    this.indexedDB.changePicturesStatus(PictureStatus.UPLOADING);
    const promises: Promise<TyreAnalysisResponseDto>[] = [];
    for (const picture of pictures) {
      promises.push(firstValueFrom(this.createTyreAnalysis(picture)));
    }
    return forkJoin([...promises, of(pictures), of(analysis)]);
  }

  private uploadPictures(value: [...TyreAnalysisResponseDto[], TyrePicture[], Analysis]) {
    const analysis = value.pop() as Analysis;
    const pictures = value.pop() as TyrePicture[];
    const responses = value as TyreAnalysisResponseDto[];
    pictures.forEach((picture, index) => {
      picture.uploadInfo = responses[index];
      this.storeService.setPicture(picture);
      this.uploadService.uploadPicture(picture);
    });
    return of(analysis);
  }
}
